/**
 * YawmiWidget — context-aware "today" card on the home feed.
 *
 * Detects the user's current temporal context (Friday morning? Eid eve?
 * Ramadan? Inner-circle birthday?) and surfaces the most relevant
 * wishing/reminder CTA. Single-tap opens the composer, pre-filled with
 * the right occasion.
 *
 * Priority engine (subset, full version is on the roadmap):
 *   1. Eid al-Fitr eve / day (Hijri 1 Shawwāl) → Eid Mubarak composer
 *   2. Eid al-Adha eve / day (Hijri 10 Dhū al-Ḥijjah) → Eid al-Adha composer
 *   3. Ramadan eve (Hijri 30 Shaʿbān → 1 Ramaḍān) → Ramadan Kareem composer
 *   4. Friday 06:00–11:00 local → Jumu'ah Mubarak composer
 *   5. Friday all day → "Send Jumu'ah Mubarak to family" CTA
 *   6. Default → "Send a wish" entry CTA
 *
 * Yawmi is also planned as a standalone app for full day-scheduling +
 * habit tracking. This widget is the home-page surface; tap "Open Yawmi"
 * → /yawmi/ (currently 404 / WIP — link kept for future).
 */

import type { Widget, WidgetContext } from '../types';
import { getCurrentSpecialDay, type SpecialDayInfo } from '../../../services/IslamicCalendar';
import { showWishingComposer, type WishingOccasionId } from '../WishingCardComposer';
import { getContext } from '../../../context/AweniContext';

// BOUNDARY (see docs/yawmi-vs-memoriz-boundary.md):
//   - "What religious day is it today?" = Memoriz-domain knowledge,
//     consumed via `IslamicCalendar.getCurrentSpecialDay()`.
//   - "What prompt do we show + when do we surface it" = Yawmi-domain
//     scheduling, owned by this widget.
//   - "What Arabic text goes in the card" = Memoriz-domain content,
//     currently hardcoded in WishingCardComposer with TODO marker for
//     extraction once Memoriz exposes `getQuoteForOccasion()`.

interface ContextSuggestion {
  occasion: WishingOccasionId;
  emoji: string;
  /** Localized title shown on the widget. Per-locale via i18n key. */
  titleKey: string;
  /** Localized subtitle. */
  subtitleKey: string;
  /** CTA label key (e.g., "Send card"). */
  ctaKey: string;
  gradient: string;
}

/**
 * Map a Memoriz-domain `SpecialDayInfo` to a Yawmi-domain UI suggestion.
 * Pure scheduling/UI decision — what prompt + tone to show. No Islamic
 * content here. Returns null when there's no special day → widget shows
 * the neutral CTA.
 */
function pickSuggestion(special: SpecialDayInfo | null): ContextSuggestion | null {
  if (!special) return null;

  switch (special.id) {
    case 'eid_fitr':
      return {
        occasion: 'eid_fitr', emoji: '🌙',
        titleKey: 'yawmi_eid_fitr_title',
        subtitleKey: 'yawmi_eid_fitr_subtitle',
        ctaKey: 'yawmi_eid_send',
        gradient: 'linear-gradient(135deg,#F59E0B,#EF4444)',
      };
    case 'eid_fitr_eve':
      return {
        occasion: 'eid_fitr', emoji: '🌙',
        titleKey: 'yawmi_eid_fitr_eve_title',
        subtitleKey: 'yawmi_eid_fitr_eve_subtitle',
        ctaKey: 'yawmi_eid_send',
        gradient: 'linear-gradient(135deg,#F59E0B,#EF4444)',
      };
    case 'eid_adha':
    case 'arafah':
      return {
        occasion: 'eid_adha', emoji: '🐑',
        titleKey: 'yawmi_eid_adha_title',
        subtitleKey: 'yawmi_eid_adha_subtitle',
        ctaKey: 'yawmi_eid_send',
        gradient: 'linear-gradient(135deg,#8B5CF6,#EC4899)',
      };
    case 'ramadan_eve':
    case 'ramadan':
      return {
        occasion: 'ramadan', emoji: '🌙',
        titleKey: 'yawmi_ramadan_title',
        subtitleKey: 'yawmi_ramadan_subtitle',
        ctaKey: 'yawmi_ramadan_send',
        gradient: 'linear-gradient(135deg,#0EA5E9,#6366F1)',
      };
    case 'jumuah': {
      // Yawmi decides the tone (morning vs afternoon) since this is
      // a scheduling/UX choice, not an Islamic detection.
      const isPeakWindow = special.hour >= 6 && special.hour < 14;
      return {
        occasion: 'jumuah', emoji: '🕌',
        titleKey: isPeakWindow ? 'yawmi_jumuah_title' : 'yawmi_jumuah_late_title',
        subtitleKey: isPeakWindow ? 'yawmi_jumuah_subtitle' : 'yawmi_jumuah_late_subtitle',
        ctaKey: 'yawmi_jumuah_send',
        gradient: 'linear-gradient(135deg,#0A0A0A,#2A2520)',
      };
    }
    // Other special days (mawlid, ashura, islamic_new_year, laylat_qadr)
    // don't yet have a wishing-card occasion — fall back to neutral.
    default:
      return null;
  }
}

function injectStyles(): void {
  if (document.getElementById('aweni-yawmi-styles')) return;
  const style = document.createElement('style');
  style.id = 'aweni-yawmi-styles';
  style.textContent = `
    .aweni-yawmi-card {
      border-radius: 18px;
      padding: 16px 18px;
      color: #fff;
      display: flex;
      flex-direction: column;
      gap: 10px;
    }
    .aweni-yawmi-card.neutral {
      background: var(--card-bg);
      border: 1px solid var(--border);
      color: var(--text-primary);
    }
    .aweni-yawmi-header {
      display: flex; align-items: center; gap: 10px;
    }
    .aweni-yawmi-emoji { font-size: 26px; line-height: 1; }
    .aweni-yawmi-title { flex: 1; font-size: 16px; font-weight: 700; line-height: 1.25; }
    .aweni-yawmi-subtitle {
      font-size: 13px;
      opacity: 0.92;
      line-height: 1.4;
    }
    .aweni-yawmi-card.neutral .aweni-yawmi-subtitle { color: var(--text-secondary); opacity: 1; }
    .aweni-yawmi-cta {
      align-self: flex-start;
      background: rgba(255,255,255,0.22);
      color: #fff;
      border: none;
      padding: 9px 16px;
      border-radius: 12px;
      font-size: 13px;
      font-weight: 700;
      cursor: pointer;
      min-height: 40px;
    }
    .aweni-yawmi-cta:hover { background: rgba(255,255,255,0.32); }
    .aweni-yawmi-card.neutral .aweni-yawmi-cta {
      background: var(--accent);
      color: var(--accent-text);
    }
  `;
  document.head.appendChild(style);
}

export const YawmiWidget: Widget = {
  id: 'yawmi',
  label: { en: 'Yawmi', fr: 'Yawmi', ar: 'يومي', es: 'Yawmi', de: 'Yawmi' },
  icon: '📅',
  defaultVisible: true,
  defaultOrder: 2,
  defaultSize: 'compact',

  render(container: HTMLElement, _size, ctx: WidgetContext): void {
    injectStyles();
    const t = ctx.t;
    // Memoriz-domain query: what's special about today, religiously?
    const special = getCurrentSpecialDay(new Date());
    // Yawmi-domain decision: what prompt to render.
    const suggestion = pickSuggestion(special);

    if (suggestion) {
      const title = t(suggestion.titleKey) || suggestion.titleKey;
      const subtitle = t(suggestion.subtitleKey) || '';
      const ctaLabel = t(suggestion.ctaKey) || 'Send';
      container.innerHTML = `
        <div class="aweni-yawmi-card" style="background:${suggestion.gradient};">
          <div class="aweni-yawmi-header">
            <span class="aweni-yawmi-emoji" aria-hidden="true">${suggestion.emoji}</span>
            <div class="aweni-yawmi-title">${title}</div>
          </div>
          ${subtitle ? `<div class="aweni-yawmi-subtitle">${subtitle}</div>` : ''}
          <button type="button" class="aweni-yawmi-cta" data-action="compose">${ctaLabel}</button>
        </div>
      `;
      const occasion = suggestion.occasion;
      container.querySelector('[data-action="compose"]')?.addEventListener('click', () => {
        const aweniCtx = getContext();
        if (aweniCtx) showWishingComposer({ ctx: aweniCtx, preselect: occasion });
      });
    } else {
      // Neutral state: simple "Send a wish" CTA, always available.
      container.innerHTML = `
        <div class="aweni-yawmi-card neutral">
          <div class="aweni-yawmi-header">
            <span class="aweni-yawmi-emoji" aria-hidden="true">💌</span>
            <div class="aweni-yawmi-title">${t('yawmi_neutral_title') || 'Send a wish to your loved ones'}</div>
          </div>
          <div class="aweni-yawmi-subtitle">${t('yawmi_neutral_subtitle') || "Eid · Jumu'ah · Birthday · Mabrouk · and more — in seconds."}</div>
          <button type="button" class="aweni-yawmi-cta" data-action="compose">${t('yawmi_neutral_cta') || 'Pick an occasion'}</button>
        </div>
      `;
      container.querySelector('[data-action="compose"]')?.addEventListener('click', () => {
        const aweniCtx = getContext();
        if (aweniCtx) showWishingComposer({ ctx: aweniCtx });
      });
    }
  },
};
